import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useDebounce } from './useDebounce';

export const useListSearch = ({
  queryKeyPrefix,
  fetchSuggestions,
  debounceMs = 300,
  minChars = 1,
  enabled = true,
}) => {
  const [input, setInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const debouncedInput = useDebounce(input, debounceMs);

  const { data, isFetching } = useQuery({
    queryKey: [...queryKeyPrefix, 'suggestions', debouncedInput],
    queryFn: () => fetchSuggestions(debouncedInput),
    enabled: enabled && debouncedInput.trim().length >= minChars,
    staleTime: 20_000,
  });

  const commitSearch = useCallback((value) => {
    if (value && typeof value === 'object') {
      setInput(String(value.label || '').trim());
      setAppliedSearch(String(value.value ?? value.label ?? '').trim());
      return;
    }
    const v = String(value ?? input ?? '').trim();
    setInput(v);
    setAppliedSearch(v);
  }, [input]);

  const clearSearch = useCallback(() => {
    setInput('');
    setAppliedSearch('');
  }, []);

  return {
    input,
    setInput,
    appliedSearch,
    commitSearch,
    clearSearch,
    suggestions: data?.suggestions ?? [],
    suggestionsLoading: isFetching,
  };
};

export const buildLocalSuggestions = (items, q, limit = 8) => {
  const needle = String(q || '').trim().toLowerCase();
  if (!needle) return [];
  const seen = new Set();
  const out = [];
  for (const item of items) {
    const label = String(item.label || '').trim();
    if (!label || !label.toLowerCase().includes(needle)) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
    if (out.length >= limit) break;
  }
  return out;
};
